import {
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  startOfMonth,
  startOfWeek,
  subMonths,
  subWeeks,
} from 'date-fns'
import { CalendarIcon } from '@radix-ui/react-icons'
import { useState } from 'react'
import type { DateRange } from 'react-day-picker'

import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

type DateRangePickerProps = {
  from: Date
  to: Date
  onChange: (range: { from: Date; to: Date }) => void
}

export function DateRangePicker({ from, to, onChange }: DateRangePickerProps) {
  const [open, setOpen] = useState(false)
  const [isMobile] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 640px)').matches : false,
  )

  const dateRange = { from, to }

  const applyRange = (range: { from?: Date; to?: Date }, close = false) => {
    if (range.from && range.to) {
      onChange({ from: range.from, to: range.to })
      if (close) setOpen(false)
    }
  }

  const selectThisWeek = () => {
    const today = new Date()
    applyRange({ from: startOfWeek(today), to: today })
    setOpen(false)
  }

  const selectLastWeek = () => {
    const lastWeek = subWeeks(new Date(), 1)
    applyRange({ from: startOfWeek(lastWeek), to: endOfWeek(lastWeek) })
    setOpen(false)
  }

  const selectLastMonth = () => {
    const lastMonth = subMonths(new Date(), 1)
    applyRange({ from: startOfMonth(lastMonth), to: endOfMonth(lastMonth) })
    setOpen(false)
  }

  const isThisWeek = () => {
    const today = new Date()
    return isSameDay(dateRange.from, startOfWeek(today)) && isSameDay(dateRange.to, today)
  }

  const isLastWeek = () => {
    const lastWeek = subWeeks(new Date(), 1)
    return isSameDay(dateRange.from, startOfWeek(lastWeek)) && isSameDay(dateRange.to, endOfWeek(lastWeek))
  }

  const isLastMonth = () => {
    const lastMonth = subMonths(new Date(), 1)
    return isSameDay(dateRange.from, startOfMonth(lastMonth)) && isSameDay(dateRange.to, endOfMonth(lastMonth))
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className={cn('w-[240px] justify-start text-left font-normal')}>
          <CalendarIcon className="mr-2 size-4" />
          {format(dateRange.from, 'MMM d, yyyy')} - {format(dateRange.to, 'MMM d, yyyy')}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="range"
          defaultMonth={isMobile ? dateRange.from : subMonths(dateRange.to, 1)}
          selected={{ from: dateRange.from, to: dateRange.to } satisfies DateRange}
          onSelect={(range) => applyRange(range ?? {}, true)}
          disabled={(date) => date > new Date()}
          numberOfMonths={isMobile ? 1 : 2}
          required
        />
        <div className="flex flex-col gap-2 border-t p-3 sm:flex-row sm:flex-wrap">
          <Button type="button" variant={isThisWeek() ? 'default' : 'outline'} size="sm" onClick={selectThisWeek}>
            This week
          </Button>
          <Button type="button" variant={isLastWeek() ? 'default' : 'outline'} size="sm" onClick={selectLastWeek}>
            Last week
          </Button>
          <Button type="button" variant={isLastMonth() ? 'default' : 'outline'} size="sm" onClick={selectLastMonth}>
            Last month
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
